import { Location } from '../components/googlemaps/location';
import { WithDates, getDates, getDateStrings, fetchJson } from '../util';

interface FlightLegModel {
    organizationLogo: string | null,
    tailNumber: string
    aircraftTypeName: string,
    aircraftTypeCode: string,
    flightAwareFlightIds: string[],
    departureLocation?: Location;
    arrivalLocation?: Location;
    departure?: string,
    departureCity?: string,
    departureState?: string,
    departureCountry?: string,
    arrival?: string,
    arrivalCity?: string,
    arrivalState?: string,
    arrivalCountry: string,
    flightAwareArrivalUTC?: string;
    flightAwareArrivalLocal?: string;
    flightAwareDepartureUTC?: string;
    flightAwareDepartureLocal?: string;
    flightAwareEstimatedArrivalUTC?: string;
    flightAwareEstimatedArrivalLocal?: string;
    scheduledArrivalUTC?: string;
    scheduledArrivalLocal?: string;
    scheduledDepartureUTC?: string;
    scheduledDepartureLocal?: string;
    arrivalTimeZoneOffset?: number;
    departureTimeZoneOffset?: number;
    expired: boolean;
}

interface FlightLegModelDates {
    flightAwareEstimatedArrivalUTC: {
        date: 'flightAwareEstimatedArrivalDateUTC',
        time: 'flightAwareEstimatedArrivalTimeUTC'
    },
    flightAwareEstimatedArrivalLocal: {
        date: 'flightAwareEstimatedArrivalDateLocal',
        time: 'flightAwareEstimatedArrivalTimeLocal'
    },
    scheduledArrivalUTC: {
        date: 'scheduledArrivalDateUTC',
        time: 'scheduledArrivalTimeUTC'
    },
    scheduledArrivalLocal: {
        date: 'scheduledArrivalDateLocal',
        time: 'scheduledArrivalTimeLocal'
    },
    flightAwareArrivalUTC: {
        date: 'flightAwareArrivalDateUTC',
        time: 'flightAwareArrivalTimeUTC'
    },
    flightAwareArrivalLocal: {
        date: 'flightAwareArrivalDateLocal',
        time: 'flightAwareArrivalTimeLocal'
    },
    scheduledDepartureUTC: {
        date: 'scheduledDepartureDateUTC',
        time: 'scheduledDepartureTimeUTC'
    },
    scheduledDepartureLocal: {
        date: 'scheduledDepartureDateLocal',
        time: 'scheduledDepartureTimeLocal'
    }
    flightAwareDepartureUTC: {
        date: 'flightAwareDepartureDateUTC',
        time: 'flightAwareDepartureTimeUTC'
    },
    flightAwareDepartureLocal: {
        date: 'flightAwareDepartureDateLocal',
        time: 'flightAwareDepartureTimeLocal'
    },
}

const FlightLegModelDates: FlightLegModelDates = {
    flightAwareEstimatedArrivalUTC: {
        date: 'flightAwareEstimatedArrivalDateUTC',
        time: 'flightAwareEstimatedArrivalTimeUTC'
    },
    flightAwareEstimatedArrivalLocal: {
        date: 'flightAwareEstimatedArrivalDateLocal',
        time: 'flightAwareEstimatedArrivalTimeLocal'
    },
    scheduledArrivalUTC: {
        date: 'scheduledArrivalDateUTC',
        time: 'scheduledArrivalTimeUTC'
    },
    scheduledArrivalLocal: {
        date: 'scheduledArrivalDateLocal',
        time: 'scheduledArrivalTimeLocal'
    },
    flightAwareArrivalUTC: {
        date: 'flightAwareArrivalDateUTC',
        time: 'flightAwareArrivalTimeUTC'
    },
    flightAwareArrivalLocal: {
        date: 'flightAwareArrivalDateLocal',
        time: 'flightAwareArrivalTimeLocal'
    },
    scheduledDepartureUTC: {
        date: 'scheduledDepartureDateUTC',
        time: 'scheduledDepartureTimeUTC'
    },
    scheduledDepartureLocal: {
        date: 'scheduledDepartureDateLocal',
        time: 'scheduledDepartureTimeLocal'
    },
    flightAwareDepartureUTC: {
        date: 'flightAwareDepartureDateUTC',
        time: 'flightAwareDepartureTimeUTC'
    },
    flightAwareDepartureLocal: {
        date: 'flightAwareDepartureDateLocal',
        time: 'flightAwareDepartureTimeLocal'
    },
}

export interface FlightLegViewModel extends WithDates<FlightLegModel, FlightLegModelDates> {
    flightAwareEstimatedArrivalDateLocal: string | null;
    flightAwareEstimatedArrivalTimeLocal: string | null,
    flightAwareEstimatedArrivalDateUTC: string | null;
    flightAwareEstimatedArrivalTimeUTC: string | null,
    flightAwareArrivalDateLocal: string | null,
    flightAwareArrivalTimeLocal: string | null,
    flightAwareArrivalDateUTC: string | null,
    flightAwareArrivalTimeUTC: string | null,
    scheduledArrivalDateLocal: string,
    scheduledArrivalTimeLocal: string,
    scheduledArrivalDateUTC: string,
    scheduledArrivalTimeUTC: string,

    flightAwareDepartureDateLocal: string | null,
    flightAwareDepartureTimeLocal: string | null,
    flightAwareDepartureDateUTC: string | null;
    flightAwareDepartureTimeUTC: string | null,
    scheduledDepartureDateLocal: string,
    scheduledDepartureTimeLocal: string,
    scheduledDepartureDateUTC: string,
    scheduledDepartureTimeUTC: string,
}

function map(model: FlightLegModel): FlightLegViewModel {
    const dates = getDates(model, FlightLegModelDates);
    return {
        ...model,
        ...dates,
        ...getDateStrings(dates, FlightLegModelDates, 'flightAwareEstimatedArrivalUTC'),
        ...getDateStrings(dates, FlightLegModelDates, 'flightAwareEstimatedArrivalLocal'),
        ...getDateStrings(dates, FlightLegModelDates, 'scheduledArrivalUTC'),
        ...getDateStrings(dates, FlightLegModelDates, 'scheduledArrivalLocal'),
        ...getDateStrings(dates, FlightLegModelDates, 'scheduledDepartureUTC'),
        ...getDateStrings(dates, FlightLegModelDates, 'scheduledDepartureLocal'),
        ...getDateStrings(dates, FlightLegModelDates, 'flightAwareDepartureUTC'),
        ...getDateStrings(dates, FlightLegModelDates, 'flightAwareDepartureLocal'),
        ...getDateStrings(dates, FlightLegModelDates, 'flightAwareArrivalUTC'),
        ...getDateStrings(dates, FlightLegModelDates, 'flightAwareArrivalLocal')
    }
}

export function fetch(url: string) {
    return fetchJson<FlightLegModel>(url)
        .then(map);
}

export function fetchMany(url: string) {
    return fetchJson<FlightLegModel[]>(url)
        .then(m => m.map(map));
}
